import Store from '@ember-data/store';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import BulkActionModel, {
  type BulkActionCustomSetCustomProperty,
  type BulkActionResponseIncludesCustomPropertyInfo,
} from 'donor-elf-web/models/bulk-action';
import type { ContactCustomProperty } from 'donor-elf-web/models/contact';
import ContactsCache from 'donor-elf-web/services/contacts-cache';
import type { BulkActionsContactsChangedArgs } from '..';
import type { ContactSaveNewPropertyArgs } from '../../custom-property/add/new';

interface ComponentArgs {
  contactIds: string[];
  onContactsChanged: (args: BulkActionsContactsChangedArgs) => void;
  onClose: () => void;
}

export default class ContactBulkActionsEditPropertyAddNewComponent extends Component<ComponentArgs> {
  @service declare contactsCache: ContactsCache;
  @service declare store: Store;

  bulkAction?: BulkActionModel;
  @tracked waitingOnBulkActionToComplete = false;
  @tracked saving = false;
  @tracked saveError = false;
  @tracked tempCustomProperty?: ContactCustomProperty;

  @action
  async handleOnSave(saveArgs: ContactSaveNewPropertyArgs) {
    this.saving = true;
    this.tempCustomProperty = {
      l: saveArgs.l,
      t: saveArgs.t,
      v: saveArgs.v,
    };
    this.bulkAction = this.store.createRecord('bulk-action');
    this.bulkAction.actionType = 'set_custom_property';
    (this.bulkAction.custom as BulkActionCustomSetCustomProperty) = {
      contact_ids: this.args.contactIds,
      new_property: true,
      l: saveArgs.l,
      v: saveArgs.v,
      t: saveArgs.t,
      o: saveArgs.o,
      s: saveArgs.s,
    };
    await this.bulkAction.save();
    this.waitingOnBulkActionToComplete = true;
  }

  @action
  async handleBulkActionStatusChange(bulkAction: BulkActionModel) {
    if (bulkAction.status === 'completed') {
      try {
        let updatedCustomPropertyInfo = (
          bulkAction.response! as BulkActionResponseIncludesCustomPropertyInfo
        ).custom_property_info;
        if (updatedCustomPropertyInfo) {
          this.contactsCache.updateCustomPropertyInfo(
            updatedCustomPropertyInfo,
          );
        }
        await bulkAction.destroyRecord();
        if (this.args.contactIds.length < 250) {
          await this.contactsCache.reloadSpecificContacts(this.args.contactIds);
        } else {
          await this.contactsCache.loadContacts({ forceLoad: true });
        }
        this.args.onContactsChanged({
          contactIds: this.args.contactIds,
          bulkActionType: 'set_custom_property',
          newPropertyAdded: true,
        });
        this.saving = false;
        this.args.onClose();
      } catch (error: any) {
        this.saveError = true;
      }
    } else if (bulkAction.status === 'error') {
      this.saveError = true;
    }
  }
}
